import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  Divider,
} from '@mui/material';
import { formatDeviceType } from '../../../utils/deviceTypes';

const ApproveRegistrationDialog = ({
  open,
  onClose,
  registration,
  onApprove,
  onDeny,
  isLoading = false,
}) => {
  const [notes, setNotes] = useState('');
  const [action, setAction] = useState(null);

  const handleApprove = () => {
    setAction('approve');
    onApprove(notes.trim() || undefined);
    
    setNotes('');
    setAction(null);
  };

  const handleDeny = () => {
    setAction('deny');
    onDeny(notes.trim() || undefined);
    
    setNotes('');
    setAction(null);
  };

  const handleClose = () => {
    setAction(null);
    onClose();
  };

  if (!registration) return null;

  const requestType = registration.request_type || registration.registration_type;
  const isNewDevice = requestType === 'new_device';
  const isOwnershipTransfer = requestType === 'ownership_transfer';
  const isDeviceReplacement = requestType === 'device_replacement';

  const macAddress = isNewDevice 
    ? registration.requested_mac_addr 
    : (registration.device_detail?.mac_addr || 'N/A');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {action === 'approve' ? 'Approve' : action === 'deny' ? 'Deny' : 'Review'} Request
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Device Information
          </Typography>
          <Typography variant="body2">
            <strong>MAC Address:</strong> {macAddress}
            {isNewDevice && registration.requested_mac_addr && (
              <Typography component="span" variant="caption" color="info.main" sx={{ ml: 1 }}>
                (New Device Request)
              </Typography>
            )}
          </Typography>
          
          {(registration.requested_device_type || registration.device_type) && (
            <Typography variant="body2">
              <strong>Device Type:</strong> {formatDeviceType(registration.requested_device_type || registration.device_type)}
            </Typography>
          )}
          
          {(registration.requested_pi || registration.pi) && (
            <Typography variant="body2">
              <strong>PI:</strong> {registration.requested_pi || registration.pi}
            </Typography>
          )}

          {isNewDevice && registration.requested_mac_addr && (
            <Typography variant="body2" sx={{ mt: 1, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
              <strong>Requested MAC Address:</strong> {registration.requested_mac_addr}
            </Typography>
          )}

          {isOwnershipTransfer && registration.requested_to_detail && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Transfer To:</strong> {registration.requested_to_detail.display_name || registration.requested_to_detail.email || 'N/A'}
              {registration.requested_to_detail.email && registration.requested_to_detail.display_name && (
                <Typography variant="caption" display="block" color="textSecondary">
                  {registration.requested_to_detail.email}
                </Typography>
              )}
            </Typography>
          )}

          {isDeviceReplacement && registration.replacement_device_detail && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Replacement Device:</strong> {registration.replacement_device_detail.mac_addr || 'N/A'}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Request Details
          </Typography>
          <Typography variant="body2">
            <strong>Type:</strong> {registration.request_type_display || registration.registration_type_display || ((registration.request_type === 'tailscale_approval' || registration.registration_type === 'tailscale_approval') ? 'Tailscale Approval' : 'Device Registration')}
          </Typography>
          <Typography variant="body2">
            <strong>Status:</strong> {registration.status_display || registration.status || 'N/A'}
          </Typography>
          <Box>
            <Typography variant="body2">
              <strong>Requested By:</strong> {registration.requested_by_detail?.display_name || registration.requested_by_detail?.email || 'N/A'}
            </Typography>
            {registration.requested_by_detail?.email && registration.requested_by_detail?.display_name && (
              <Typography variant="caption" display="block" color="textSecondary">
                {registration.requested_by_detail.email}
              </Typography>
            )}
          </Box>
          <Typography variant="body2">
            <strong>Requested Date:</strong> {(registration.requested_at || registration.requested_date) ? new Date(registration.requested_at || registration.requested_date).toLocaleString() : 'N/A'}
          </Typography>
          {registration.expiry_date && (
            <Typography variant="body2">
              <strong>Expiry Date:</strong> {new Date(registration.expiry_date).toLocaleString()}
            </Typography>
          )}
          {(registration.reviewed_by_detail || registration.approved_by_detail) && (
            <Typography variant="body2">
              <strong>Reviewed By:</strong> {(registration.reviewed_by_detail || registration.approved_by_detail).display_name || (registration.reviewed_by_detail || registration.approved_by_detail).email || 'N/A'}
            </Typography>
          )}
          {(registration.reviewed_at || registration.approved_date) && (
            <Typography variant="body2">
              <strong>Reviewed Date:</strong> {new Date(registration.reviewed_at || registration.approved_date).toLocaleString()}
            </Typography>
          )}
          {registration.completed_at && (
            <Typography variant="body2">
              <strong>Completed At:</strong> {new Date(registration.completed_at).toLocaleString()}
            </Typography>
          )}
          {registration.rejection_reason && (
            <Typography variant="body2" sx={{ mt: 1, color: 'error.main' }}>
              <strong>Rejection Reason:</strong> {registration.rejection_reason}
            </Typography>
          )}
          {registration.reason && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Reason:</strong> {registration.reason}
            </Typography>
          )}
          {registration.notes && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Notes:</strong> {registration.notes}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <TextField
          label="Admin Notes (Optional)"
          fullWidth
          multiline
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes about your decision..."
          disabled={isLoading || action !== null}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading || action !== null}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={handleDeny}
          disabled={isLoading || action !== null}
          startIcon={action === 'deny' && isLoading ? <CircularProgress size={16} /> : null}
        >
          Deny
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleApprove}
          disabled={isLoading || action !== null}
          startIcon={action === 'approve' && isLoading ? <CircularProgress size={16} /> : null}
        >
          Approve
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApproveRegistrationDialog;



